import { Fragment } from 'react';
import { Link } from 'react-router-dom';
import { ArrowTopRightOnSquareIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import type { AssetDetail } from '@xphoto/shared';
import { useI18n } from '../i18n';
import { folderLinkFor } from '../gallery/url-state';
import { filePathSegments } from '../gallery/folder-path';
import {
  formatBytes,
  formatDateTime,
  formatDuration,
  formatGpsCoords,
  googleMapsUrl,
  gpsFromExif,
  gpsInsertIndex,
  selectExif,
} from './format';

/**
 * Panneau « Détail » de la visionneuse (XPHOTO-21) : LECTURE SEULE. Volontairement
 * ALLÉGÉ (retour utilisateur v0.49.0) : plus d'éditeurs de tags/notes, ni de ligne « Nom
 * de fichier » (déjà dans le chemin), ni de section « Empreintes » (empreintes techniques
 * inutiles ici). Dimensions et Taille tiennent sur UNE seule ligne. L'édition de la note
 * (★) et des commentaires vit dans la barre supérieure (`Viewer`), à part.
 */
interface InfoPanelProps {
  detail: AssetDetail;
}

/**
 * Style des liens du panneau — aligné sur les liens existants sur `bg-surface`
 * (cf. `ai/LlmConfigPage`) : couleur de marque + SOULIGNEMENT au survol et au focus
 * clavier, contour de focus visible. Le survol/focus ne repose donc jamais sur la
 * couleur SEULE (WCAG AA).
 */
const LINK_CLASS =
  'text-brand underline-offset-2 hover:underline focus-visible:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand';

/**
 * Ligne de définition (terme + valeur), masquée quand il n'y a rien à afficher.
 *
 * `value` est un `ReactNode` (et plus une simple chaîne) : la ligne « Chemin » y place
 * des `<Link>` par segment. Le masquage automatique reste porté par l'appelant, qui
 * passe `null` quand la donnée est absente.
 *
 * `labelHidden` garde le `<dt>` dans le DOM mais le rend invisible (`sr-only`) : le
 * `<dl>` reste une liste de définitions valide et un lecteur d'écran sait toujours de
 * quoi la valeur parle, alors que l'œil ne voit que la valeur (demande utilisateur
 * pour le chemin, dont le libellé « Chemin » n'apporte rien visuellement).
 */
function Row({
  label,
  value,
  labelHidden = false,
}: {
  label: string;
  value: React.ReactNode;
  labelHidden?: boolean;
}): React.JSX.Element | null {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className="flex flex-col gap-0.5 py-1">
      <dt className={labelHidden ? 'sr-only' : 'text-xs font-medium text-muted'}>{label}</dt>
      <dd className="break-words text-sm text-fg">{value}</dd>
    </div>
  );
}

export function InfoPanel({ detail }: InfoPanelProps): React.JSX.Element {
  const { t, locale } = useI18n();

  const dimensions =
    detail.width !== null && detail.height !== null ? `${detail.width} × ${detail.height}` : null;
  const size = detail.sizeBytes !== null ? formatBytes(detail.sizeBytes, locale) : null;
  // Dimensions et Taille sur une SEULE ligne (ex. « 640 × 360 · 2,2 Mio »).
  const dimensionsAndSize =
    dimensions !== null && size !== null ? `${dimensions} · ${size}` : (dimensions ?? size);
  const exifPairs = selectExif(detail.exifSnapshot);

  // Chemin CLIQUABLE façon explorateur (même cible d'URL que le fil d'Ariane) : chaque
  // dossier ancêtre ouvre l'onglet « Dossiers » sur lui, le nom de fichier pointe vers la
  // route canonique de CETTE photo. Ce dernier lien est sans effet au clic (on y est
  // déjà) — c'est voulu : il donne le « Copier l'adresse du lien » du menu contextuel.
  // Cliquer un dossier NAVIGUE et referme donc la visionneuse (overlay de route), comme
  // le fait déjà le chemin de la légende. Entrée dégénérée (`relPath` vide) ⇒ `null`,
  // et `Row` masque la ligne.
  const { folders, fileName } = filePathSegments(detail.relPath);
  const pathValue =
    fileName === '' ? null : (
      <>
        {folders.map((segment) => (
          <Fragment key={segment.path}>
            <Link to={folderLinkFor(segment.path)} className={LINK_CLASS}>
              {segment.name}
            </Link>
            {/* Séparateur : texte NON cliquable, et lu comme le « / » d'un chemin. */}
            <span className="text-muted">{' / '}</span>
          </Fragment>
        ))}
        <Link to={`/photo/${detail.id}`} className={LINK_CLASS}>
          {fileName}
        </Link>
      </>
    );

  // GPS : UNE ligne (« GPS lat/long ») recomposée et signée, à la place qu'occupaient les
  // paires brutes `GPSLatitude`/`GPSLongitude` dans l'ordre EXIF. Absent/invalide ⇒ pas
  // de ligne du tout (aucun substitut). Cf. `gpsFromExif` pour le pourquoi du helper.
  const gps = gpsFromExif(detail.exifSnapshot);
  const gpsAt = gpsInsertIndex(exifPairs);

  return (
    <div className="flex flex-col gap-4">
      {/* Divergence disque ↔ base. */}
      <section aria-label={t('info.divergence')} className="flex flex-col gap-1">
        <h3 className="text-sm font-semibold text-fg">{t('info.divergence')}</h3>
        {detail.isMissing ? (
          <p className="inline-flex items-center gap-1 text-sm text-danger">
            <ExclamationTriangleIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
            {t('info.missing')}
          </p>
        ) : (
          <p className="text-sm text-muted">{t('info.present')}</p>
        )}
        {formatDateTime(detail.seenAt, locale) !== null && (
          <p className="text-xs text-muted">
            {t('info.seenAt')} : {formatDateTime(detail.seenAt, locale)}
          </p>
        )}
      </section>

      {/* Métadonnées principales (lecture seule). */}
      <section aria-label={t('info.title')}>
        <h3 className="text-sm font-semibold text-fg">{t('info.title')}</h3>
        <dl className="divide-y divide-border">
          {/* Libellé « Chemin » INVISIBLE (sr-only) : le chemin se lit tout seul. */}
          <Row label={t('info.path')} value={pathValue} labelHidden />
          <Row label={t('info.mime')} value={detail.mime} />
          <Row label={t('info.dimensions')} value={dimensionsAndSize} />
          <Row label={t('info.takenAt')} value={formatDateTime(detail.takenAt, locale)} />
          <Row
            label={t('info.duration')}
            value={detail.durationS !== null ? formatDuration(detail.durationS) : null}
          />
        </dl>
      </section>

      {/* EXIF brut sélectionné (lecture seule). */}
      <section aria-label={t('info.exif')} className="flex flex-col gap-1">
        <h3 className="text-sm font-semibold text-fg">{t('info.exif')}</h3>
        {/* « Aucune donnée EXIF » seulement si RIEN à montrer : un snapshot réduit au
            GPS a bien une section (la ligne GPS), pas le message d'absence. */}
        {exifPairs.length === 0 && gps === null ? (
          <p className="text-sm text-muted">{t('info.noExif')}</p>
        ) : (
          <dl className="divide-y divide-border">
            {exifPairs.slice(0, gpsAt).map((p) => (
              <Row key={p.key} label={p.key} value={p.value} />
            ))}
            {gps !== null && (
              <Row
                label={t('info.gps')}
                value={
                  // Lien EXTERNE (nouvel onglet) : `noopener noreferrer` obligatoire.
                  // L'icône « lien externe » le signale AUTREMENT que par la couleur.
                  // Le nom accessible reste les COORDONNÉES (contenu du lien) ; `title`
                  // en donne la description (« Ouvrir dans Google Maps ») sans l'écraser
                  // — ce qu'un `aria-label` aurait fait.
                  <a
                    href={googleMapsUrl(gps)}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={t('info.gpsOpen')}
                    className={`inline-flex items-center gap-1 ${LINK_CLASS}`}
                  >
                    {formatGpsCoords(gps)}
                    <ArrowTopRightOnSquareIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
                  </a>
                }
              />
            )}
            {exifPairs.slice(gpsAt).map((p) => (
              <Row key={p.key} label={p.key} value={p.value} />
            ))}
          </dl>
        )}
      </section>
    </div>
  );
}
